import { useEffect, useState } from 'react';
import { CheckCircle2, Clock, XCircle, Phone, Search, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import AssistantChat from '../components/AssistantChat.jsx';
import { tasksAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { formatDateTime, formatPhone } from '../lib/format.js';

// The EXECUTIVE-ADMIN centerpiece: a task-first view of what the chief-of-staff is
// working on, bucketed exactly as the owner thinks about it —
//   ✅ Completed · ⏳ In progress · ❌ Not completed (+ the reason).
// Reads the unified GET /tasks (call_tasks + research_tasks). Not campaign/contact-
// first like the sales dashboard — it leads with the WORK and its outcome.

const BUCKETS = [
  {
    key: 'completed',
    label: 'Completed',
    icon: CheckCircle2,
    tone: 'text-emerald-300',
    ring: 'border-emerald-800/60',
    empty: 'Nothing completed yet.'
  },
  {
    key: 'pending',
    label: 'In progress',
    icon: Clock,
    tone: 'text-amber-300',
    ring: 'border-amber-800/60',
    empty: 'No tasks in progress.'
  },
  {
    key: 'notCompleted',
    label: "Couldn't complete",
    icon: XCircle,
    tone: 'text-rose-300',
    ring: 'border-rose-800/60',
    empty: 'Nothing failed — good news.'
  }
];

function KindBadge({ task }) {
  const isResearch = task.kind === 'research';
  return (
    <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px] inline-flex items-center gap-1">
      {isResearch ? <Search size={10} /> : <Phone size={10} />}
      {task.modeLabel || (isResearch ? 'Research' : 'Call')}
    </span>
  );
}

function TaskCard({ task, bucketKey }) {
  const target = task.kind === 'research' ? task.target : formatPhone(task.target);
  return (
    <li className="rounded-xl border border-stone-800 bg-stone-950/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-stone-100 font-medium">{task.title}</span>
            <KindBadge task={task} />
          </div>
          {task.detail && (
            <p className="mt-1 text-sm text-stone-400 leading-relaxed line-clamp-3">
              {task.detail}
            </p>
          )}
        </div>
        {target && (
          <span className="shrink-0 text-xs text-stone-500 whitespace-nowrap">{target}</span>
        )}
      </div>

      {/* The OUTCOME — the reason it couldn't complete, or how a finished one went. */}
      {bucketKey === 'notCompleted' && task.reason && (
        <p className="mt-3 rounded-lg border border-rose-900/40 bg-rose-950/30 px-3 py-2 text-sm text-rose-200">
          <span className="font-medium">Reason:</span> {task.reason}
        </p>
      )}
      {bucketKey === 'completed' && task.outcome && (
        <p className="mt-3 rounded-lg border border-emerald-900/40 bg-emerald-950/20 px-3 py-2 text-sm text-emerald-100">
          {task.outcome}
        </p>
      )}

      <div className="mt-3 flex items-center gap-3 text-xs text-stone-600">
        <span>{formatDateTime(task.createdAt)}</span>
        {task.onBehalfOf && <span>· for {task.onBehalfOf}</span>}
        {task.kind === 'research' && (
          <span>· {task.called}/{task.found} called</span>
        )}
        {task.mode === 'scheduled' && task.scheduledAt && (
          <span>· scheduled {formatDateTime(task.scheduledAt)}</span>
        )}
      </div>
    </li>
  );
}

export default function Tasks() {
  const { tenant, selectedTenant, isOwner } = useAuth();
  const shownTenant = (isOwner && selectedTenant) || tenant;
  // The assistant chat is available to tenants with a task dashboard (exec-admin +
  // the lighter 'tasks' profile, e.g. the Screener).
  const hasChat = !!shownTenant?.features?.executiveAdmin || shownTenant?.features?.dashboard === 'tasks';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await tasksAPI.list();
      setData(r);
    } catch (err) {
      if (err.status === 404) {
        setData({ buckets: { completed: [], pending: [], notCompleted: [] }, counts: {} });
      } else {
        setError(err.message || 'Failed to load tasks.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = data?.counts || {};

  return (
    <div>
      <PageHeader
        title="Tasks"
        subtitle="What your assistant is working on — and how each one turned out."
        actions={
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      />

      {hasChat && (
        <div className="mb-6">
          <AssistantChat onTasksCreated={load} />
        </div>
      )}

      {error && (
        <p className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {loading && !data ? (
        <p className="text-sm text-stone-500">Loading tasks…</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {BUCKETS.map((b) => {
            const items = data?.buckets?.[b.key] || [];
            const Icon = b.icon;
            return (
              <section key={b.key} className={`card border ${b.ring}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon size={18} className={b.tone} />
                  <h2 className="text-lg text-stone-100">{b.label}</h2>
                  <span className="ml-auto text-sm text-stone-500">
                    {counts[b.key] ?? items.length}
                  </span>
                </div>
                {items.length === 0 ? (
                  <p className="text-sm text-stone-600">{b.empty}</p>
                ) : (
                  <ul className="space-y-3">
                    {items.map((t) => (
                      <TaskCard key={`${t.kind}:${t.id}`} task={t} bucketKey={b.key} />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
